import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import Hoot from "./Hoot";
import { Avatar, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import {
  FiTarget, FiX, FiPlus, FiEdit2, FiTrash2,
  FiSave, FiEye, FiEyeOff, FiRefreshCw,
  FiCalendar, FiUsers, FiAward, FiTrendingUp,
  FiMaximize, FiMinimize, FiMonitor,
} from "react-icons/fi";

// ─── STORAGE ──────────────────────────────────────────────────────────────────
const STORAGE_KEY = "educrm_goals_v2";
const PINNED_KEY  = "educrm_goals_pinned_v2";

// ─── GOAL PALETTE ─────────────────────────────────────────────────────────────
const PALETTE = [
  "#6052ff","#10b981","#f59e0b","#ef4444","#8b5cf6",
  "#06b6d4","#f97316","#ec4899","#14b8a6","#84cc16",
];

// ─── IDLE CONFIG ──────────────────────────────────────────────────────────────
const IDLE_MS        = 2 * 60 * 1000;  // 5 minutes inactivity
const IDLE_ROTATE_MS = 20 * 1000;      // rotate quote every 10 s

// ─── 44 QUOTES (idle screen) ──────────────────────────────────────────────────
const IDLE_QUOTES = [
  { text:"O seu tempo é limitado. Não o desperdice a viver a vida de outra pessoa.", author:"Steve Jobs", role:"Co-fundador da Apple", bg:"#16a34a", accent:"#a3e635" },
  { text:"Regra n.º 1: Nunca perca dinheiro. Regra n.º 2: Nunca se esqueça da regra n.º 1.", author:"Warren Buffett", role:"CEO da Berkshire Hathaway", bg:"#1d4ed8", accent:"#93c5fd" },
  { text:"Se não conseguir tornar-se grande fazendo o que ama, pelo menos fará algo que ama.", author:"Oprah Winfrey", role:"Media mogul & filantropa", bg:"#7c3aed", accent:"#e879f9" },
  { text:"A inovação distingue um líder de um seguidor.", author:"Steve Jobs", role:"Co-fundador da Apple", bg:"#0f766e", accent:"#5eead4" },
  { text:"Não te preocupes com o fracasso. Preocupa-te com as hipóteses que perdes quando não tentas.", author:"Jack Ma", role:"Fundador do Alibaba", bg:"#b45309", accent:"#fcd34d" },
  { text:"O sucesso não é definitivo. O fracasso não é fatal. O que conta é a coragem de continuar.", author:"Winston Churchill", role:"Ex-Primeiro Ministro britânico", bg:"#1e3a5f", accent:"#60a5fa" },
  { text:"A única forma de fazer um trabalho excelente é amar o que se faz.", author:"Steve Jobs", role:"Co-fundador da Apple", bg:"#065f46", accent:"#34d399" },
  { text:"Não estamos a tentar ser a empresa maior. Estamos a tentar ser a melhor.", author:"Jeff Bezos", role:"Fundador da Amazon", bg:"#1c1c2e", accent:"#a78bfa" },
  { text:"A vida é 10% o que te acontece e 90% como reagiste.", author:"Charles R. Swindoll", role:"Autor & pastor americano", bg:"#9d174d", accent:"#f9a8d4" },
  { text:"O preço da excelência é a disciplina. O custo da mediocridade é o arrependimento.", author:"William Arthur Ward", role:"Escritor motivacional", bg:"#1e40af", accent:"#bfdbfe" },
  { text:"Sonha em grande. Começa pequeno. Age agora.", author:"Robin Sharma", role:"Autor de 'O Monge que Vendeu o Ferrari'", bg:"#dc2626", accent:"#fca5a5" },
  { text:"Não temos de ser grandes para começar, mas temos de começar para ser grandes.", author:"Zig Ziglar", role:"Escritor & orador motivacional", bg:"#14532d", accent:"#86efac" },
  { text:"O verdadeiro poder não vem do ter, mas do dar.", author:"Elon Musk", role:"CEO da Tesla & SpaceX", bg:"#1a1a2e", accent:"#818cf8" },
  { text:"Primeiro eles ignoram-te, depois riem de ti, depois combatem-te, depois tu vences.", author:"Mahatma Gandhi", role:"Líder independentista indiano", bg:"#78350f", accent:"#fde68a" },
  { text:"Seja a mudança que deseja ver no mundo.", author:"Mahatma Gandhi", role:"Líder independentista indiano", bg:"#166534", accent:"#bbf7d0" },
  { text:"A ambição é o caminho para o sucesso. A persistência é o veículo que lá chega.", author:"Bill Bradley", role:"Político e ex-atleta americano", bg:"#0c4a6e", accent:"#7dd3fc" },
  { text:"O sucesso é ir de fracasso em fracasso sem perder o entusiasmo.", author:"Winston Churchill", role:"Ex-Primeiro Ministro britânico", bg:"#312e81", accent:"#c4b5fd" },
  { text:"Não é o mais forte da espécie que sobrevive. É o mais adaptável à mudança.", author:"Charles Darwin", role:"Naturalista e biólogo", bg:"#064e3b", accent:"#6ee7b7" },
  { text:"Cada especialista já foi um dia principiante.", author:"Helen Hayes", role:"Actriz premiada com Oscar", bg:"#4c1d95", accent:"#ddd6fe" },
  { text:"Não deixes que o barulho das opiniões dos outros abafe a tua própria voz interior.", author:"Steve Jobs", role:"Co-fundador da Apple", bg:"#134e4a", accent:"#99f6e4" },
  { text:"O risco maior é não arriscar nada. Num mundo que muda muito depressa, a única estratégia que garante fracasso é não arriscar.", author:"Mark Zuckerberg", role:"CEO da Meta", bg:"#1e3a8a", accent:"#93c5fd" },
  { text:"A vida encolhe ou expande-se em proporção directa à coragem de cada um.", author:"Anaïs Nin", role:"Escritora franco-americana", bg:"#831843", accent:"#fbcfe8" },
  { text:"A única pessoa que és destinado a tornar-te é a pessoa que decides ser.", author:"Ralph Waldo Emerson", role:"Filósofo e poeta americano", bg:"#365314", accent:"#bef264" },
  { text:"A inteligência é a capacidade de adaptar-se à mudança.", author:"Stephen Hawking", role:"Físico e cosmólogo", bg:"#0f172a", accent:"#38bdf8" },
  { text:"Não conta quantas vezes caíste. Conta quantas vezes te levantaste.", author:"Nelson Mandela", role:"Ex-Presidente da África do Sul", bg:"#7c2d12", accent:"#fed7aa" },
  { text:"O futuro pertence àqueles que acreditam na beleza dos seus sonhos.", author:"Eleanor Roosevelt", role:"Diplomata e activista americana", bg:"#1e1b4b", accent:"#c7d2fe" },
  { text:"A melhor maneira de prever o futuro é criá-lo.", author:"Peter Drucker", role:"Pai da gestão moderna", bg:"#1d4ed8", accent:"#bfdbfe" },
  { text:"Rodeia-te dos melhores e o teu resultado será o melhor.", author:"Warren Buffett", role:"CEO da Berkshire Hathaway", bg:"#14532d", accent:"#bbf7d0" },
  { text:"A melhor forma de começar é parar de falar e começar a fazer.", author:"Walt Disney", role:"Fundador da Walt Disney Company", bg:"#172554", accent:"#93c5fd" },
  { text:"O fracasso é simplesmente a oportunidade de começar de novo, desta vez mais inteligente.", author:"Henry Ford", role:"Fundador da Ford Motor Company", bg:"#3f3f46", accent:"#e4e4e7" },
  { text:"Nunca confundas movimento com acção.", author:"Ernest Hemingway", role:"Escritor Nobel da Literatura", bg:"#7c3aed", accent:"#ddd6fe" },
  { text:"O segredo de avançar é começar.", author:"Mark Twain", role:"Escritor e humorista americano", bg:"#0c4a6e", accent:"#7dd3fc" },
  { text:"Não podes ligar os pontos olhando para a frente. Só consegues ligá-los olhando para trás.", author:"Steve Jobs", role:"Co-fundador da Apple", bg:"#16a34a", accent:"#bbf7d0" },
  { text:"A maior glória não está em nunca cair, mas em cada vez que caímos, levantarmo-nos.", author:"Confúcio", role:"Filósofo chinês", bg:"#7f1d1d", accent:"#fca5a5" },
  { text:"Foca-te em ser produtivo em vez de estar ocupado.", author:"Tim Ferriss", role:"Autor de 'A Semana de 4 Horas'", bg:"#065f46", accent:"#6ee7b7" },
  { text:"A motivação faz-te começar. O hábito faz-te continuar.", author:"Jim Ryun", role:"Atleta olímpico americano", bg:"#1c1c2e", accent:"#818cf8" },
  { text:"O único lugar onde o sucesso vem antes do trabalho é no dicionário.", author:"Vidal Sassoon", role:"Empresário e empreendedor", bg:"#78350f", accent:"#fde68a" },
  { text:"Sê corajoso. Toma riscos. Nada pode substituir a experiência.", author:"Paulo Coelho", role:"Escritor de 'O Alquimista'", bg:"#134e4a", accent:"#5eead4" },
  { text:"Se tens uma ideia mas não actuas sobre ela, essa ideia nunca te tornará melhor.", author:"Jack Ma", role:"Fundador do Alibaba", bg:"#7f1d1d", accent:"#fca5a5" },
  { text:"Aquilo que consegues obter não te tornará feliz. A pessoa em que te transformas tornará.", author:"Zig Ziglar", role:"Escritor & orador motivacional", bg:"#9d174d", accent:"#fbcfe8" },
  { text:"O talento é mais barato que o sal de mesa. O que separa o talentoso do bem-sucedido é muito trabalho árduo.", author:"Stephen King", role:"Escritor bestseller", bg:"#1a0a2e", accent:"#e879f9" },
  { text:"Toda a acção humana, seja boa ou má, começa com um pensamento.", author:"Blaise Pascal", role:"Matemático e filósofo", bg:"#1e3a5f", accent:"#bfdbfe" },
  { text:"A equipa que celebra junta, vence junta.", author:"Anónimo", role:"Sabedoria popular", bg:"#1d4ed8", accent:"#93c5fd" },
  { text:"Podes falhar em muitas coisas, mas enquanto não desistires, nunca és um fracassado.", author:"Albert Einstein", role:"Físico e Nobel da Física", bg:"#1a3a2a", accent:"#4ade80" },
];

// ─── 50 TICKER PHRASES (fullscreen goals overlay bottom) ──────────────────────
const PHRASES = [
  "A distância entre o sonho e a realidade chama-se acção.",
  "Não contes os dias — faz os dias contar.",
  "O sucesso é a soma de pequenos esforços repetidos dia após dia.",
  "Quem corre atrás do vento, colhe tempestade. Quem trabalha, colhe resultados.",
  "A motivação põe-te em movimento; o hábito mantém-te em marcha.",
  "Grandes resultados exigem grandes ambições.",
  "Cada cliente conquistado é um passo em direcção à excelência.",
  "O único lugar onde o sucesso vem antes do trabalho é no dicionário.",
  "Não há atalhos para qualquer lugar que valha a pena ir.",
  "Age como se o impossível fosse apenas uma questão de tempo.",
  "A persistência é o caminho do êxito.",
  "Quem não arrisca, não petisca — e não alcança.",
  "O teu trabalho de hoje é o teu legado de amanhã.",
  "Cada 'não' recebido é treino para o próximo 'sim'.",
  "Faz hoje o que os outros não querem, para amanhã teres o que os outros não têm.",
  "O esforço de hoje é a confiança de amanhã.",
  "Disciplina é liberdade.",
  "Não esperes pela oportunidade certa — cria-a.",
  "O campeão não nasce campeão; torna-se campeão.",
  "Pequenas vitórias diárias constroem grandes conquistas.",
  "A excelência não é um destino, é uma prática contínua.",
  "Se não és tu, quem? Se não é agora, quando?",
  "O fracasso é apenas o pretexto para recomeçar com mais experiência.",
  "Vai mais longe do que ontem — sempre.",
  "O talento ganha jogos, mas o trabalho em equipa ganha campeonatos.",
  "A tua energia hoje determina os teus resultados amanhã.",
  "Não há vento favorável para quem não sabe para onde vai.",
  "O segredo do sucesso está na consistência do propósito.",
  "Conquista o teu dia antes que o dia te conquiste a ti.",
  "Cada chamada, cada visita, cada esforço — conta.",
  "A venda começa quando o cliente diz não.",
  "Sê tão bom que não te possam ignorar.",
  "Confia no processo — os resultados chegam para quem não desiste.",
  "Uma meta clara vale mais do que mil intenções vagas.",
  "Não é o mais forte que sobrevive, mas o mais adaptável.",
  "O maior risco é não arriscar nada.",
  "Foca-te no progresso, não na perfeição.",
  "Os vencedores fazem o que os perdedores não querem fazer.",
  "O entusiasmo é a fagulha; a determinação é o fogo.",
  "Trabalha em silêncio — que o sucesso faça o ruído por ti.",
  "Cada dia é uma nova oportunidade de superar quem eras ontem.",
  "A grandeza não está na chegada, mas na jornada.",
  "Não há limite para o que consegues alcançar quando trabalhas com propósito.",
  "O sucesso não é acidente; é trabalho duro, perseverança, aprendizagem e sacrifício.",
  "Quem tem um porquê suporta qualquer como.",
  "Faz do teu trabalho a tua obra de arte.",
  "Os obstáculos são aquelas coisas assustadoras que vês quando tiras os olhos do objectivo.",
  "Cada meta alcançada é uma prova de que sonhar e agir funcionam juntos.",
  "A persistência vence o talento quando o talento não persiste.",
  "A equipa que celebra junta, vence junta.",
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function parsePicture(stored) {
  if (!stored) return null;
  const s = String(stored);
  if (s.includes("|")) return s.split("|")[0];
  return s.replace(/(\.(jpg|jpeg|png|webp|gif)).*$/i, "$1");
}
function loadGoals()   { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; } }
function saveGoals(g)  { localStorage.setItem(STORAGE_KEY, JSON.stringify(g)); }
function loadPinned()  { try { return JSON.parse(localStorage.getItem(PINNED_KEY) || "[]"); } catch { return []; } }
function savePinned(p) { localStorage.setItem(PINNED_KEY, JSON.stringify(p)); }
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
}
function daysLeft(end) {
  if (!end) return null;
  return Math.ceil((new Date(end) - new Date()) / 86400000);
}
function pct(current, target) {
  if (!target || target <= 0) return 0;
  return Math.min(100, Math.round((current / target) * 100));
}
function initials(name) {
  return (name || "?").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
}
function lighten(hex, amount = 0.85) {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  const m = c => Math.round(c + (255 - c) * amount);
  return `rgb(${m(r)},${m(g)},${m(b)})`;
}
function darken(hex, amount = 0.6) {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  const m = c => Math.round(c * (1 - amount));
  return `rgb(${m(r)},${m(g)},${m(b)})`;
}
function fmtTime() {
  return new Date().toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}
function fmtDateFull() {
  return new Date().toLocaleDateString("pt-PT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}
function getCountdown(endDate) {
  if (!endDate) return null;
  const diff = new Date(endDate).setHours(23, 59, 59, 999) - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, mins: 0, secs: 0, expired: true };
  return {
    days:  Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    mins:  Math.floor((diff % 3600000) / 60000),
    secs:  Math.floor((diff % 60000) / 1000),
    expired: false,
  };
}
function urgencyLevel(goal) {
  const p = pct(goal.currentCount, goal.targetCount);
  if (p >= 100) return "done";
  if (!goal.endDate) return "nodate";
  const cd = getCountdown(goal.endDate);
  if (!cd || cd.expired) return "critical";
  const dr = cd.days + cd.hours / 24, prog = p / 100;
  const totalDays = goal.startDate ? Math.max(1, (new Date(goal.endDate) - new Date(goal.startDate)) / 86400000) : 30;
  const elapsed   = goal.startDate ? Math.max(0, (Date.now() - new Date(goal.startDate)) / 86400000) : totalDays - dr;
  const lag = Math.min(1, elapsed / totalDays) - prog;
  if (dr < 3 && p < 80) return "critical";
  if (dr < 7 && lag > 0.25) return "critical";
  if (lag > 0.3 || dr < 5) return "warning";
  return "ok";
}
const URGENCY_MSG = {
  critical: "⚠ Pouco tempo para cumprir as metas!",
  warning:  "📊 Atenção — o ritmo actual pode não ser suficiente.",
  ok:       "✅ No caminho certo. Mantém o ritmo!",
  done:     "🏆 Meta alcançada! Excelente trabalho!",
  nodate:   "🎯 Sem prazo definido — foca-te no crescimento contínuo.",
};
const URGENCY_COLORS = {
  critical: "#ef4444", warning: "#f59e0b", ok: "#10b981", done: "#6ee7b7", nodate: "#94a3b8",
};
const zp = n => String(n).padStart(2, "0");

// ─── STYLES ───────────────────────────────────────────────────────────────────
const css = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,700&family=Inter:wght@400;500;600&display=swap');

/* ── FAB ── */
.gt-fab-group{ position:fixed; bottom:28px; right:28px; z-index:1300; display:flex; flex-direction:column; align-items:center; gap:10px; }
.gt-fab{ width:52px; height:52px; border-radius:50%; background:var(--primary,#6052ff); color:#fff; border:none; box-shadow:0 6px 24px rgba(96,82,255,.38); display:grid; place-items:center; cursor:pointer; font-size:22px; transition:transform .12s ease,box-shadow .12s ease; }
.gt-fab:hover{ transform:scale(1.08); box-shadow:0 10px 32px rgba(96,82,255,.48); }
.gt-fab:active{ transform:scale(.97); }
.gt-fab-fs{ width:44px; height:44px; border-radius:50%; background:var(--card,#fff); border:1px solid var(--border,#e2e8f0); box-shadow:0 4px 16px rgba(2,6,23,.12); display:grid; place-items:center; cursor:pointer; font-size:18px; color:var(--text,#0f172a); transition:transform .12s ease,background .12s ease; }
.gt-fab-fs:hover{ transform:scale(1.08); background:var(--primary-soft,#ebe9ff); color:var(--primary,#6052ff); }
.gt-fab-fs.active-fs{ background:var(--primary,#6052ff); color:#fff; border-color:var(--primary,#6052ff); box-shadow:0 6px 24px rgba(96,82,255,.5); animation:gt-pulse 2s infinite; }
@keyframes gt-pulse{ 0%,100%{ box-shadow:0 6px 24px rgba(96,82,255,.5); } 50%{ box-shadow:0 6px 36px rgba(96,82,255,.85); } }

/* ── SIDE MODAL ── */
.gt-backdrop{ position:fixed; inset:0; background:rgba(2,6,23,.52); z-index:1310; opacity:0; pointer-events:none; transition:opacity .18s ease; }
.gt-backdrop.open{ opacity:1; pointer-events:auto; }
.gt-modal{ position:fixed; top:0; right:0; height:100vh; width:min(780px,100vw); background:var(--card,#fff); border-left:1px solid var(--border,#e2e8f0); z-index:1320; transform:translateX(105%); transition:transform .22s ease; display:flex; flex-direction:column; box-shadow:-8px 0 48px rgba(2,6,23,.14); }
.gt-modal.open{ transform:translateX(0); }
.gt-header{ padding:16px 18px 14px; border-bottom:1px solid var(--border,#e2e8f0); display:flex; align-items:center; justify-content:space-between; gap:12px; flex-shrink:0; }
.gt-body{ flex:1; overflow-y:auto; padding:16px 18px 24px; }
.gt-tabs{ display:flex; gap:8px; flex-wrap:wrap; margin-bottom:16px; }
.gt-tab{ border-radius:999px; border:1px solid rgba(148,163,184,.35); background:transparent; padding:7px 14px; font-size:.82rem; font-weight:800; color:var(--muted,#6b7280); cursor:pointer; display:inline-flex; align-items:center; gap:6px; transition:background .12s,border-color .12s,color .12s; }
.gt-tab.active{ background:var(--primary-soft,#ebe9ff); border-color:var(--primary-border,#d1ceff); color:var(--primary,#6052ff); }

/* ── FORM PIECES ── */
.gt-user-row{ display:grid; grid-template-columns:1fr; gap:12px; }
@media(min-width:520px){ .gt-user-row{ grid-template-columns:1fr 1fr; } }
.gt-user-card{ border:1px solid var(--border,#e2e8f0); border-radius:18px; padding:14px; background:var(--card,#fff); display:flex; flex-direction:column; gap:10px; cursor:pointer; transition:transform .10s,border-color .12s; }
.gt-user-card:hover{ transform:translateY(-1px); border-color:rgba(96,82,255,.35); }
.gt-user-card.selected{ border-color:var(--primary,#6052ff); background:var(--primary-soft,#ebe9ff); }
.gt-prog-wrap{ width:100%; border-radius:999px; background:rgba(148,163,184,.18); overflow:visible; position:relative; }
.gt-prog-fill{ height:100%; border-radius:999px; transition:width .5s cubic-bezier(.4,0,.2,1); position:relative; overflow:visible; }
.gt-prog-avatar{ position:absolute; right:-14px; top:50%; transform:translateY(-50%); width:28px; height:28px; border-radius:50%; border:2px solid #fff; overflow:hidden; background:rgba(148,163,184,.22); display:grid; place-items:center; font-size:.65rem; font-weight:900; transition:right .5s cubic-bezier(.4,0,.2,1); z-index:2; flex-shrink:0; }
.gt-prog-avatar img{ width:100%; height:100%; object-fit:cover; }
.gt-goal-card{ border:1px solid var(--border,#e2e8f0); border-radius:18px; padding:14px; background:var(--card,#fff); margin-bottom:12px; }
.gt-goal-header{ display:flex; align-items:flex-start; justify-content:space-between; gap:10px; margin-bottom:10px; }
.gt-badge{ display:inline-flex; align-items:center; gap:6px; border-radius:999px; padding:3px 10px; font-size:.72rem; font-weight:950; border:1px solid rgba(148,163,184,.25); }
.gt-form-card{ border:1px solid var(--border,#e2e8f0); border-radius:18px; padding:14px; background:var(--card,#fff); margin-bottom:14px; }
.gt-label{ font-size:.78rem; color:var(--muted,#6b7280); font-weight:950; margin-bottom:5px; }
.gt-input{ width:100%; border:1px solid rgba(148,163,184,.35); border-radius:12px; padding:9px 12px; background:var(--card,#fff); color:var(--text,#0f172a); font-size:.88rem; font-weight:650; }
.gt-input:focus{ outline:none; border-color:var(--primary,#6052ff); }
.gt-grid-2{ display:grid; grid-template-columns:1fr 1fr; gap:10px; }
@media(max-width:460px){ .gt-grid-2{ grid-template-columns:1fr; } }
.gt-color-row{ display:flex; flex-wrap:wrap; gap:8px; }
.gt-color-dot{ width:26px; height:26px; border-radius:50%; cursor:pointer; border:3px solid transparent; transition:transform .08s,border-color .08s; }
.gt-color-dot:hover{ transform:scale(1.12); }
.gt-color-dot.sel{ border-color:#0f172a; }
.gt-btn{ border-radius:999px; border:1px solid rgba(148,163,184,.35); background:var(--card,#fff); padding:8px 16px; font-size:.84rem; font-weight:850; cursor:pointer; display:inline-flex; align-items:center; gap:6px; transition:background .12s,border-color .12s; }
.gt-btn:hover{ background:rgba(148,163,184,.10); }
.gt-btn.primary{ background:var(--primary,#6052ff); color:#fff; border-color:var(--primary,#6052ff); }
.gt-btn.primary:hover{ background:#4e42e0; }
.gt-btn.sm{ padding:5px 12px; font-size:.78rem; }
.gt-icon-btn{ width:34px; height:34px; border-radius:12px; border:1px solid rgba(148,163,184,.35); background:rgba(148,163,184,.08); display:grid; place-items:center; cursor:pointer; color:var(--text,#0f172a); transition:background .12s; }
.gt-icon-btn:hover{ background:rgba(96,82,255,.10); border-color:rgba(96,82,255,.28); }

/* ── PINNED BAR ── */
.gt-pinned-bar{ position:fixed; top:0; left:0; right:0; z-index:1400; background:var(--card,#fff); border-bottom:1px solid var(--border,#e2e8f0); padding:6px 14px; display:flex; align-items:center; gap:14px; flex-wrap:nowrap; overflow-x:auto; scrollbar-width:none; box-shadow:0 2px 12px rgba(2,6,23,.08); }
.gt-pinned-bar::-webkit-scrollbar{ display:none; }
.gt-pinned-item{ display:flex; align-items:center; gap:8px; flex-shrink:0; min-width:180px; max-width:240px; }
.gt-pinned-prog{ flex:1; height:8px; border-radius:999px; background:rgba(148,163,184,.18); overflow:hidden; }
.gt-pinned-fill{ height:100%; border-radius:999px; transition:width .5s ease; }
.gt-pinned-label{ font-size:.72rem; font-weight:900; white-space:nowrap; color:var(--text,#0f172a); }
.gt-no-goals{ text-align:center; padding:32px 0; color:var(--muted,#6b7280); font-size:.86rem; font-weight:650; }
.gt-section-title{ font-size:.72rem; font-weight:950; text-transform:uppercase; letter-spacing:.06em; color:var(--muted,#6b7280); margin-bottom:10px; }

/* ── GOALS FULLSCREEN ── */
.gt-fs-overlay{ position:fixed; inset:0; z-index:9999; background:#060818; display:flex; flex-direction:column; overflow:hidden; opacity:0; pointer-events:none; transition:opacity .3s ease; }
.gt-fs-overlay.show{ opacity:1; pointer-events:auto; }
.gt-fs-topbar{ padding:14px 28px 0; display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
.gt-fs-title{ font-size:1.3rem; font-weight:950; color:#fff; letter-spacing:.02em; display:flex; align-items:center; gap:10px; }
.gt-fs-clock{ font-size:.82rem; color:rgba(255,255,255,.45); font-weight:600; letter-spacing:.03em; font-variant-numeric:tabular-nums; }
.gt-fs-actions{ display:flex; gap:8px; }
.gt-fs-btn{ border-radius:10px; border:1px solid rgba(255,255,255,.15); background:rgba(255,255,255,.08); padding:7px 14px; font-size:.8rem; font-weight:850; cursor:pointer; display:inline-flex; align-items:center; gap:6px; color:#fff; transition:background .14s; }
.gt-fs-btn:hover{ background:rgba(255,255,255,.16); }
.gt-fs-btn.exit{ border-color:rgba(239,68,68,.4); color:#fca5a5; }
.gt-fs-btn.exit:hover{ background:rgba(239,68,68,.12); }
.gt-fs-grid{ flex:1; padding:12px 24px 8px; display:grid; gap:14px; overflow:hidden; min-height:0; }
.gt-fs-grid[data-count="1"]{ grid-template-columns:1fr; max-width:680px; margin:0 auto; width:100%; }
.gt-fs-grid[data-count="2"]{ grid-template-columns:1fr 1fr; }
.gt-fs-grid[data-count="3"]{ grid-template-columns:1fr 1fr 1fr; }
.gt-fs-grid[data-count="4"]{ grid-template-columns:1fr 1fr; grid-template-rows:1fr 1fr; }
.gt-fs-grid[data-count="5"],.gt-fs-grid[data-count="6"]{ grid-template-columns:repeat(3,1fr); grid-template-rows:1fr 1fr; }
.gt-fs-grid[data-count-many]{ grid-template-columns:repeat(auto-fill,minmax(320px,1fr)); }
.gt-fs-card{ border-radius:20px; border:1px solid rgba(255,255,255,.08); background:rgba(255,255,255,.04); padding:18px 20px 14px; display:flex; flex-direction:column; gap:10px; min-height:0; overflow:hidden; position:relative; transition:transform .15s ease,border-color .2s ease; }
.gt-fs-card:hover{ transform:translateY(-2px); border-color:rgba(255,255,255,.14); }
.gt-fs-card.completed{ border-color:rgba(16,185,129,.4); animation:gt-completed-pulse 3s ease infinite; }
.gt-fs-card.gt-rank-1{ box-shadow:0 0 32px rgba(251,191,36,.2); }
@keyframes gt-completed-pulse{ 0%,100%{ background:rgba(16,185,129,.04); } 50%{ background:rgba(16,185,129,.10); } }
.gt-fs-card-top{ display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }
.gt-fs-card-name{ font-size:1rem; font-weight:950; color:#fff; line-height:1.2; }
.gt-fs-card-sub{ font-size:.78rem; color:rgba(255,255,255,.48); font-weight:650; margin-top:2px; }
.gt-fs-avatar{ flex-shrink:0; width:48px; height:48px; border-radius:50%; overflow:hidden; display:grid; place-items:center; font-size:.95rem; font-weight:950; border:2px solid rgba(255,255,255,.15); }
.gt-fs-avatar img{ width:100%; height:100%; object-fit:cover; }
.gt-fs-prog-track{ width:100%; border-radius:999px; background:rgba(255,255,255,.08); overflow:visible; position:relative; flex-shrink:0; }
.gt-fs-prog-fill{ height:100%; border-radius:999px; position:relative; overflow:visible; transition:width .8s cubic-bezier(.4,0,.2,1); }
.gt-fs-prog-glow{ position:absolute; inset:0; border-radius:999px; opacity:.4; filter:blur(7px); transition:width .8s cubic-bezier(.4,0,.2,1); }
.gt-fs-prog-runner{ position:absolute; top:50%; transform:translateY(-50%); border-radius:50%; border:3px solid #060818; overflow:hidden; display:grid; place-items:center; font-weight:950; z-index:2; transition:right .8s cubic-bezier(.4,0,.2,1); }
.gt-fs-prog-runner img{ width:100%; height:100%; object-fit:cover; }
.gt-fs-stats{ display:flex; align-items:center; justify-content:space-between; gap:8px; flex-wrap:wrap; }
.gt-fs-pct{ font-weight:950; line-height:1; letter-spacing:-.02em; }
.gt-fs-counts{ text-align:right; }
.gt-fs-count-main{ font-weight:950; color:#fff; }
.gt-fs-count-sub{ color:rgba(255,255,255,.42); font-weight:650; }

/* Countdown box */
.gt-cd-box{ background:rgba(255,255,255,.05); border-radius:12px; padding:8px 10px; border:1px solid rgba(255,255,255,.07); flex-shrink:0; }
.gt-cd-row{ display:flex; align-items:flex-end; gap:4px; justify-content:center; flex-wrap:wrap; }
.gt-cd-label{ font-size:.55rem; color:rgba(255,255,255,.38); font-weight:700; letter-spacing:.04em; margin-bottom:1px; align-self:flex-end; }
.gt-cd-sep{ color:rgba(255,255,255,.2); font-size:.9rem; font-weight:300; align-self:flex-end; margin-bottom:2px; line-height:1; }
.gt-cd-unit{ text-align:center; }
.gt-cd-num{ font-variant-numeric:tabular-nums; letter-spacing:-.01em; line-height:1; font-weight:950; }
.gt-cd-sub{ font-size:.52rem; color:rgba(255,255,255,.3); font-weight:700; margin-top:1px; letter-spacing:.04em; }

.gt-fs-urgency{ font-weight:950; text-align:center; letter-spacing:.01em; flex-shrink:0; }
.gt-fs-logo{ position:absolute; bottom:8px; right:14px; font-size:.6rem; color:rgba(255,255,255,.16); font-weight:950; letter-spacing:.08em; text-transform:uppercase; }
.gt-fs-empty{ flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; color:rgba(255,255,255,.35); gap:14px; }
.gt-fs-phrase-bar{ flex-shrink:0; padding:7px 28px 12px; text-align:center; border-top:1px solid rgba(255,255,255,.05); transition:opacity .5s ease; }

/* ── IDLE QUOTE SCREEN ── */
.iq-overlay{ position:fixed; inset:0; z-index:19999; display:flex; align-items:center; justify-content:center; overflow:hidden; pointer-events:none; opacity:0; transition:opacity .9s cubic-bezier(.4,0,.2,1); }
.iq-overlay.iq-visible{ opacity:1; pointer-events:auto; }
.iq-bg{ position:absolute; inset:0; transition:background-color 1.4s cubic-bezier(.4,0,.2,1); }
.iq-bg::after{ content:''; position:absolute; inset:0; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E"); background-size:180px 180px; pointer-events:none; opacity:.45; }
.iq-qmark-open{ position:absolute; top:5%; left:4%; font-family:'Playfair Display',serif; font-size:clamp(72px,13vw,175px); line-height:1; font-weight:700; user-select:none; pointer-events:none; opacity:.18; letter-spacing:-.02em; transition:color 1.4s ease; }
.iq-qmark-close{ position:absolute; bottom:9%; right:4%; font-family:'Playfair Display',serif; font-size:clamp(72px,13vw,175px); line-height:1; font-weight:700; user-select:none; pointer-events:none; opacity:.18; letter-spacing:-.02em; transition:color 1.4s ease; }
.iq-center{ position:relative; z-index:2; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; max-width:min(840px,88vw); padding:0 24px; }
.iq-text{ font-family:'Playfair Display',serif; font-size:clamp(1.4rem,3.4vw,2.85rem); font-weight:700; font-style:italic; line-height:1.3; letter-spacing:-.01em; color:#fff; opacity:0; transform:translateY(22px); transition:opacity .72s cubic-bezier(.4,0,.2,1),transform .72s cubic-bezier(.4,0,.2,1); text-shadow:0 2px 32px rgba(0,0,0,.2); }
.iq-text.iq-in{ opacity:1; transform:translateY(0); }
.iq-divider{ width:0; height:2px; border-radius:999px; margin:22px auto 18px; transition:width .62s cubic-bezier(.4,0,.2,1) .26s,background-color 1.4s ease; }
.iq-divider.iq-in{ width:clamp(38px,7vw,70px); }
.iq-author-wrap{ opacity:0; transform:translateY(12px); transition:opacity .55s cubic-bezier(.4,0,.2,1) .34s,transform .55s cubic-bezier(.4,0,.2,1) .34s; }
.iq-author-wrap.iq-in{ opacity:1; transform:translateY(0); }
.iq-author{ font-family:'Inter',sans-serif; font-size:clamp(.86rem,1.65vw,1.08rem); font-weight:600; letter-spacing:.05em; color:#fff; text-transform:uppercase; }
.iq-role{ font-family:'Inter',sans-serif; font-size:clamp(.68rem,1.2vw,.84rem); font-weight:400; letter-spacing:.02em; margin-top:5px; color:rgba(255,255,255,.5); }
.iq-progress-bar{ position:absolute; top:0; left:0; height:3px; border-radius:0 999px 999px 0; z-index:4; }
.iq-dots{ position:absolute; bottom:46px; left:50%; transform:translateX(-50%); display:flex; gap:6px; z-index:3; flex-wrap:wrap; justify-content:center; max-width:80vw; }
.iq-dot{ width:6px; height:6px; border-radius:50%; background:rgba(255,255,255,.2); transition:background .3s ease,transform .3s ease,width .3s ease; flex-shrink:0; }
.iq-dot.iq-active{ background:rgba(255,255,255,.85); transform:scale(1.3); }
.iq-bottom{ position:absolute; bottom:0; left:0; right:0; padding:14px 24px; display:flex; align-items:center; justify-content:space-between; z-index:3; }
.iq-logo{ font-family:'Inter',sans-serif; font-size:.74rem; font-weight:600; letter-spacing:.1em; color:rgba(255,255,255,.3); text-transform:uppercase; }
.iq-hint{ font-family:'Inter',sans-serif; font-size:.68rem; font-weight:400; letter-spacing:.03em; color:rgba(255,255,255,.2); display:flex; align-items:center; gap:6px; }
.iq-hint-dot{ width:6px; height:6px; border-radius:50%; background:rgba(255,255,255,.2); animation:iq-blink 2.2s ease infinite; }
@keyframes iq-blink{ 0%,100%{ opacity:.3; } 50%{ opacity:1; } }

/* ── MISC ── */
.gt-has-pinned .fixed-top{ top:44px !important; }
.gt-has-pinned .app-main-wrapper{ padding-top:100px !important; }
`;

// ─── ProgressBar (sidebar) ────────────────────────────────────────────────────
function ProgressBar({ current, target, color, picUrl, name, height = 12 }) {
  const p = pct(current, target);
  const pic = parsePicture(picUrl);
  return (
    <div style={{ position: "relative", marginBottom: 8, paddingRight: 20 }}>
      <div className="gt-prog-wrap" style={{ height }}>
        <div className="gt-prog-fill" style={{ width: `${p}%`, background: color || "#6052ff", height }}>
          <div className="gt-prog-avatar" title={`${name}: ${p}%`}>
            {pic
              ? <img src={pic} alt={name} onError={e => { e.currentTarget.style.display = "none"; }} />
              : <span style={{ color, fontSize: ".6rem", fontWeight: 900 }}>{initials(name)}</span>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Countdown (fullscreen) ───────────────────────────────────────────────────
function CountdownBlock({ endDate, color, size = "md" }) {
  const [cd, setCd] = useState(() => getCountdown(endDate));
  useEffect(() => {
    if (!endDate) return;
    const t = setInterval(() => setCd(getCountdown(endDate)), 1000);
    return () => clearInterval(t);
  }, [endDate]);

  const numSize  = size === "lg" ? "2rem"   : size === "sm" ? ".9rem"  : "1.3rem";
  const subSize  = size === "lg" ? ".6rem"  : size === "sm" ? ".5rem"  : ".55rem";
  const daySize  = size === "lg" ? "2.6rem" : size === "sm" ? "1.1rem" : "1.7rem";

  if (!endDate) return (
    <div className="gt-cd-box" style={{ textAlign: "center" }}>
      <span style={{ fontSize: ".76rem", color: "rgba(255,255,255,.3)", fontWeight: 650 }}>Sem prazo definido</span>
    </div>
  );
  if (!cd || cd.expired) return (
    <div className="gt-cd-box" style={{ textAlign: "center" }}>
      <span style={{ fontSize: ".8rem", color: "#ef4444", fontWeight: 950 }}>Prazo expirado</span>
    </div>
  );

  const urgColor = cd.days < 3 ? "#ef4444" : cd.days < 7 ? "#f59e0b" : color || "#fff";

  return (
    <div className="gt-cd-box">
      <div className="gt-cd-row">
        <span className="gt-cd-label">Faltam</span>
        {/* DAYS — biggest */}
        <div className="gt-cd-unit">
          <div className="gt-cd-num" style={{ fontSize: daySize, color: urgColor }}>{cd.days}</div>
          <div className="gt-cd-sub">dias</div>
        </div>
        <span className="gt-cd-sep">:</span>
        {/* HH */}
        <div className="gt-cd-unit">
          <div className="gt-cd-num" style={{ fontSize: numSize, color: urgColor }}>{zp(cd.hours)}</div>
          <div className="gt-cd-sub">h</div>
        </div>
        <span className="gt-cd-sep">:</span>
        {/* MM */}
        <div className="gt-cd-unit">
          <div className="gt-cd-num" style={{ fontSize: numSize, color: urgColor }}>{zp(cd.mins)}</div>
          <div className="gt-cd-sub">min</div>
        </div>
        <span className="gt-cd-sep">:</span>
        {/* SS */}
        <div className="gt-cd-unit">
          <div className="gt-cd-num" style={{ fontSize: numSize, color: urgColor }}>{zp(cd.secs)}</div>
          <div className="gt-cd-sub">seg</div>
        </div>
      </div>
    </div>
  );
}

// ─── FullscreenCard ───────────────────────────────────────────────────────────
function FullscreenCard({ goal: g, rank, totalCards }) {
  const p       = pct(g.currentCount, g.targetCount);
  const pic     = parsePicture(g.userPicture);
  const done    = p >= 100;
  const urgency = urgencyLevel(g);

  const rankColors = ["#fbbf24", "#94a3b8", "#cd7c2e"];
  const rankEmoji  = ["🥇", "🥈", "🥉"];

  // scale font/bar sizes based on number of cards
  const isSmall  = totalCards >= 5;
  const isMedium = totalCards === 3 || totalCards === 4;
  const pctSize  = isSmall ? "2rem" : isMedium ? "2.6rem" : "3.2rem";
  const barH     = isSmall ? 14 : isMedium ? 18 : 22;
  const runnerSz = isSmall ? 28 : isMedium ? 36 : 44;
  const avatarSz = isSmall ? 40 : isMedium ? 48 : 56;
  const cdSize   = isSmall ? "sm" : isMedium ? "md" : "lg";

  return (
    <div
      className={`gt-fs-card ${done ? "completed" : ""} ${rank === 1 ? "gt-rank-1" : ""}`}
      style={{
        borderColor: urgency === "critical" ? "rgba(239,68,68,.4)"  :
                     urgency === "warning"  ? "rgba(245,158,11,.28)" :
                     done                   ? "rgba(16,185,129,.4)"  :
                     "rgba(255,255,255,.08)",
      }}
    >
      {/* top accent strip + glow */}
      <div style={{ position:"absolute", top:0, left:0, right:0, height:4, borderRadius:"20px 20px 0 0", background:g.color, boxShadow:`0 0 20px ${g.color}99` }}/>

      {/* rank emoji */}
      {rank <= 3 && (
        <div style={{ position:"absolute", top:10, left:12, fontSize: isSmall ? "1rem" : "1.25rem", filter:"drop-shadow(0 2px 6px rgba(0,0,0,.5))" }}>
          {rankEmoji[rank - 1]}
        </div>
      )}

      {/* top row */}
      <div className="gt-fs-card-top" style={{ paddingLeft: rank <= 3 ? (isSmall ? 26 : 32) : 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gt-fs-card-name" style={{ fontSize: isSmall ? ".9rem" : "1rem" }}>{g.label}</div>
          <div className="gt-fs-card-sub" style={{ display:"flex", alignItems:"center", gap:6 }}>
            {g.userName}
            {rank <= 3 && (
              <span style={{ fontSize:".65rem", fontWeight:950, padding:"2px 7px", borderRadius:999, border:`1px solid ${rankColors[rank-1]}55`, background:`${rankColors[rank-1]}18`, color:rankColors[rank-1] }}>
                #{rank}
              </span>
            )}
          </div>
        </div>
        <div className="gt-fs-avatar" style={{ width:avatarSz, height:avatarSz, background:lighten(g.color,.12), borderColor:`${g.color}55` }}>
          {pic
            ? <img src={pic} alt={g.userName} onError={e => { e.currentTarget.style.display = "none"; }} />
            : <span style={{ color:g.color, fontSize: isSmall ? ".85rem":"1rem", fontWeight:950 }}>{initials(g.userName)}</span>
          }
        </div>
      </div>

      {/* big pct + counts */}
      <div className="gt-fs-stats">
        <div className="gt-fs-pct" style={{ fontSize:pctSize, color: done ? "#6ee7b7" : g.color }}>{p}%</div>
        <div className="gt-fs-counts">
          <div className="gt-fs-count-main" style={{ fontSize: isSmall ? ".9rem":"1.05rem" }}>{g.currentCount} / {g.targetCount}</div>
          <div className="gt-fs-count-sub" style={{ fontSize: isSmall ? ".68rem":".76rem" }}>
            {g.startDate ? `${fmtDate(g.startDate)} → ${fmtDate(g.endDate)}` : "Sem prazo"}
          </div>
        </div>
      </div>

      {/* progress bar with runner avatar */}
      <div style={{ paddingRight: runnerSz / 2 + 6, flexShrink:0 }}>
        <div className="gt-fs-prog-track" style={{ height: barH }}>
          <div className="gt-fs-prog-glow" style={{ width:`${p}%`, background:g.color, height:barH }}/>
          <div className="gt-fs-prog-fill" style={{ width:`${p}%`, background:`linear-gradient(90deg,${darken(g.color,.15)},${g.color})`, height:barH }}>
            <div
              className="gt-fs-prog-runner"
              style={{ width:runnerSz, height:runnerSz, right: -(runnerSz/2), background:g.color, fontSize: runnerSz > 34 ? ".75rem" : ".62rem" }}
            >
              {pic
                ? <img src={pic} alt={g.userName} onError={e => { e.currentTarget.style.display = "none"; }} />
                : <span style={{ color:"#fff", fontWeight:950 }}>{initials(g.userName)}</span>
              }
            </div>
          </div>
        </div>
      </div>

      {/* ── COUNTDOWN ── big and very visible */}
      <CountdownBlock endDate={g.endDate} color={g.color} size={cdSize} />

      {/* urgency message */}
      <div className="gt-fs-urgency" style={{ fontSize: isSmall ? ".68rem":".74rem", color: URGENCY_COLORS[urgency] || "rgba(255,255,255,.45)" }}>
        {URGENCY_MSG[urgency]}
      </div>

      <div className="gt-fs-logo">Eduall CRM</div>
    </div>
  );
}

// ─── FullscreenOverlay ────────────────────────────────────────────────────────
function FullscreenOverlay({ goals, pinned, onClose }) {
  const [clock, setClock]         = useState(fmtTime());
  const [date, setDate]           = useState(fmtDateFull());
  const [phraseIdx, setPhraseIdx] = useState(() => Math.floor(Math.random() * PHRASES.length));
  const [phraseVis, setPhraseVis] = useState(true);
  const fsRef  = useRef(null);
  const [nativeFs, setNativeFs]   = useState(false);

  const sorted = useMemo(() => {
    const raw = pinned.map(id => goals.find(g => g.id === id)).filter(Boolean);
    return [...raw].sort((a, b) => pct(b.currentCount, b.targetCount) - pct(a.currentCount, a.targetCount));
  }, [pinned, goals]);

  useEffect(() => {
    const t = setInterval(() => { setClock(fmtTime()); setDate(fmtDateFull()); }, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setPhraseVis(false);
      setTimeout(() => { setPhraseIdx(i => (i + 1) % PHRASES.length); setPhraseVis(true); }, 550);
    }, 12000);
    return () => clearInterval(t);
  }, []);

  const toggleNativeFs = () => {
    if (!document.fullscreenElement) { fsRef.current?.requestFullscreen?.().catch(() => {}); setNativeFs(true); }
    else { document.exitFullscreen?.(); setNativeFs(false); }
  };
  useEffect(() => {
    const h = () => { if (!document.fullscreenElement) setNativeFs(false); };
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  useEffect(() => {
    const h = e => { if (e.key === "Escape" && !document.fullscreenElement) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const overallUrgency = useMemo(() => {
    if (!sorted.length) return "nodate";
    const order = ["critical","warning","ok","done","nodate"];
    return sorted.map(g => urgencyLevel(g)).sort((a,b) => order.indexOf(a) - order.indexOf(b))[0];
  }, [sorted]);

  const count    = sorted.length;
  const gridAttr = count > 6 ? { "data-count-many": "" } : { "data-count": count };

  return (
    <div className="gt-fs-overlay show" ref={fsRef} style={{ display:"flex", flexDirection:"column" }}>
      {/* top bar */}
      <div className="gt-fs-topbar">
        <div className="gt-fs-title">
          <FiTarget style={{ color:"#6052ff", fontSize:22 }} />
          Metas da Equipa
          <span className="gt-fs-clock">{date} · {clock}</span>
        </div>
        <div className="gt-fs-actions">
          <button className="gt-fs-btn" onClick={toggleNativeFs}>
            {nativeFs ? <FiMinimize style={{ fontSize:14 }}/> : <FiMaximize style={{ fontSize:14 }}/>}
            {nativeFs ? "Sair ecrã" : "Ecrã completo"}
          </button>
          <button className="gt-fs-btn exit" onClick={onClose}><FiX style={{ fontSize:14 }}/> Fechar</button>
        </div>
      </div>

      {/* urgency banner */}
      {count > 0 && overallUrgency !== "done" && (
        <div style={{
          margin:"5px 24px 0", padding:"5px 16px", borderRadius:10, flexShrink:0,
          background: overallUrgency === "critical" ? "rgba(239,68,68,.12)" : overallUrgency === "warning" ? "rgba(245,158,11,.10)" : "rgba(16,185,129,.08)",
          border:`1px solid ${overallUrgency === "critical" ? "rgba(239,68,68,.28)" : overallUrgency === "warning" ? "rgba(245,158,11,.22)" : "rgba(16,185,129,.2)"}`,
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          <span style={{ fontSize:".8rem", fontWeight:950, color:URGENCY_COLORS[overallUrgency] }}>
            {URGENCY_MSG[overallUrgency]}
          </span>
        </div>
      )}

      {/* grid */}
      {count === 0 ? (
        <div className="gt-fs-empty" style={{ flex:1 }}>
          <FiTarget style={{ fontSize:48, opacity:.3 }} />
          <div style={{ fontSize:"1.1rem", fontWeight:950 }}>Sem metas afixadas</div>
          <div style={{ fontSize:".88rem" }}>Afixe metas no gestor para as ver aqui.</div>
        </div>
      ) : (
        <div className="gt-fs-grid" {...gridAttr} style={{ flex:1, minHeight:0 }}>
          {sorted.map((g, i) => <FullscreenCard key={g.id} goal={g} rank={i + 1} totalCards={count} />)}
        </div>
      )}

      {/* phrase ticker */}
      <div className="gt-fs-phrase-bar" style={{ opacity: phraseVis ? 1 : 0 }}>
        <span style={{ fontStyle:"italic", fontSize:".8rem", fontWeight:650, letterSpacing:".02em", color:"rgba(255,255,255,.3)" }}>
          "{PHRASES[phraseIdx]}"
        </span>
      </div>
    </div>
  );
}

// ─── IdleQuoteScreen ──────────────────────────────────────────────────────────
function IdleQuoteScreen() {
  const [visible,  setVisible]  = useState(false);
  const [qIdx,     setQIdx]     = useState(0);
  const [inAnim,   setInAnim]   = useState(false);
  const [progress, setProgress] = useState(0);
  const idleTimer   = useRef(null);
  const rotateTimer = useRef(null);

  const startProgress = useCallback(() => {
    setProgress(0);
    requestAnimationFrame(() => requestAnimationFrame(() => setProgress(100)));
  }, []);

  const nextQuote = useCallback(() => {
    setInAnim(false);
    setTimeout(() => {
      setQIdx(i => (i + 1) % IDLE_QUOTES.length);
      setInAnim(true);
      startProgress();
    }, 550);
  }, [startProgress]);

  const showIdle = useCallback(() => {
    setQIdx(Math.floor(Math.random() * IDLE_QUOTES.length));
    setVisible(true);
    setTimeout(() => { setInAnim(true); startProgress(); }, 80);
    rotateTimer.current = setInterval(nextQuote, IDLE_ROTATE_MS);
  }, [nextQuote, startProgress]);

  const hideIdle = useCallback(() => {
    setVisible(false); setInAnim(false);
    clearInterval(rotateTimer.current); rotateTimer.current = null;
  }, []);

  const resetIdle = useCallback(() => {
    if (visible) hideIdle();
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(showIdle, IDLE_MS);
  }, [visible, hideIdle, showIdle]);

  useEffect(() => {
    const EVTS = ["mousemove","mousedown","keydown","touchstart","scroll","click","wheel"];
    EVTS.forEach(ev => window.addEventListener(ev, resetIdle, { passive: true }));
    idleTimer.current = setTimeout(showIdle, IDLE_MS);
    return () => {
      EVTS.forEach(ev => window.removeEventListener(ev, resetIdle));
      clearTimeout(idleTimer.current);
      clearInterval(rotateTimer.current);
    };
  }, [resetIdle, showIdle]);

  const q = IDLE_QUOTES[qIdx];

  return (
    <div className={`iq-overlay ${visible ? "iq-visible" : ""}`} onClick={resetIdle}>
      <div className="iq-bg" style={{ backgroundColor: q.bg }} />

      {/* progress bar */}
      <div
        className="iq-progress-bar"
        style={{
          width: `${progress}%`,
          background: q.accent,
          opacity: .72,
          transition: progress === 0 ? "none" : `width ${IDLE_ROTATE_MS / 1000}s linear, background-color 1.4s ease`,
        }}
      />

      {/* decorative quotes */}
      <div className="iq-qmark-open"  style={{ color: q.accent }}>"</div>
      <div className="iq-qmark-close" style={{ color: q.accent }}>"</div>

      {/* center */}
      <div className="iq-center">
        <div className={`iq-text ${inAnim ? "iq-in" : ""}`}>{q.text}</div>
        <div className={`iq-divider ${inAnim ? "iq-in" : ""}`} style={{ background: q.accent }} />
        <div className={`iq-author-wrap ${inAnim ? "iq-in" : ""}`}>
          <div className="iq-author" style={{ color: q.accent }}>{q.author}</div>
          <div className="iq-role">{q.role}</div>
        </div>
      </div>

      {/* dot indicators */}
      <div className="iq-dots">
        {IDLE_QUOTES.map((_, i) => (
          <div key={i} className={`iq-dot ${i === qIdx ? "iq-active" : ""}`} />
        ))}
      </div>

      {/* bottom bar */}
      <div className="iq-bottom">
        <span className="iq-logo">Eduall CRM</span>
        <span className="iq-hint"><span className="iq-hint-dot" />Mover o rato para voltar</span>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function GoalsTracker() {
  const [open, setOpen]               = useState(false);
  const [fsOpen, setFsOpen]           = useState(false);
  const [tab, setTab]                 = useState("overview");
  const [users, setUsers]             = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [goals, setGoals]             = useState(loadGoals);
  const [pinned, setPinned]           = useState(loadPinned);
  const [selUser, setSelUser]         = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  const [form, setForm] = useState({
    label: "", targetCount: "", currentCount: "0",
    startDate: "", endDate: "", color: PALETTE[0], note: ""
  });

  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => { saveGoals(goals); }, [goals]);
  useEffect(() => { savePinned(pinned); }, [pinned]);
  useEffect(() => {
    if (pinned.length > 0) document.documentElement.classList.add("gt-has-pinned");
    else document.documentElement.classList.remove("gt-has-pinned");
    return () => document.documentElement.classList.remove("gt-has-pinned");
  }, [pinned.length]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await axios.get(Hoot() + "api/manager-accounts", { withCredentials: true });
      setUsers(res.data || []);
    } catch { /* silent */ }
    finally { setLoadingUsers(false); }
  };

  const resetForm = () => {
    setForm({ label: "", targetCount: "", currentCount: "0", startDate: "", endDate: "", color: PALETTE[0], note: "" });
    setSelUser(null); setEditingGoal(null);
  };
  const openNew  = () => { resetForm(); setTab("new"); };
  const openEdit = goal => {
    setEditingGoal(goal);
    setSelUser(users.find(u => u.id === goal.userId) || null);
    setForm({ label: goal.label||"", targetCount: String(goal.targetCount||""), currentCount: String(goal.currentCount||"0"), startDate: goal.startDate||"", endDate: goal.endDate||"", color: goal.color||PALETTE[0], note: goal.note||"" });
    setTab("new");
  };

  const saveGoal = () => {
    if (!selUser)            return toast.warning("Selecione um utilizador.");
    if (!form.label.trim())  return toast.warning("Defina um título para a meta.");
    if (!form.targetCount || isNaN(Number(form.targetCount)) || Number(form.targetCount) <= 0)
      return toast.warning("Target inválido.");
    const payload = {
      id: editingGoal?.id || `g_${Date.now()}`,
      userId: selUser.id, userName: selUser.name, userEmail: selUser.email, userPicture: selUser.picture,
      label: form.label.trim(), targetCount: Number(form.targetCount), currentCount: Number(form.currentCount)||0,
      startDate: form.startDate, endDate: form.endDate, color: form.color, note: form.note,
      createdAt: editingGoal?.createdAt || new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    if (editingGoal) { setGoals(prev => prev.map(g => g.id === editingGoal.id ? payload : g)); toast.success("Meta atualizada!"); }
    else             { setGoals(prev => [...prev, payload]); toast.success("Meta criada!"); }
    resetForm(); setTab("overview");
  };

  const deleteGoal    = id  => { setGoals(prev => prev.filter(g => g.id !== id)); setPinned(prev => prev.filter(x => x !== id)); toast.success("Meta removida."); };
  const updateCurrent = (id, val) => { setGoals(prev => prev.map(g => g.id === id ? { ...g, currentCount: Math.max(0, Number(val)||0), updatedAt: new Date().toISOString() } : g)); };
  const togglePin     = id  => { setPinned(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); };

  const pinnedGoals = useMemo(() => pinned.map(id => goals.find(g => g.id === id)).filter(Boolean), [pinned, goals]);
  const goalsByUser = useMemo(() => {
    const map = {};
    goals.forEach(g => {
      if (!map[g.userId]) map[g.userId] = { userName: g.userName, userPicture: g.userPicture, goals: [] };
      map[g.userId].goals.push(g);
    });
    return map;
  }, [goals]);

  // ── Pinned Bar ──
  const PinnedBar = () => {
    if (!pinnedGoals.length) return null;
    return (
      <div className="gt-pinned-bar">
        <span style={{ fontSize:".72rem", fontWeight:950, color:"var(--muted)", whiteSpace:"nowrap", flexShrink:0 }}>
          <FiTarget style={{ marginRight:4, verticalAlign:-2 }}/> Metas
        </span>
        {pinnedGoals.map(g => {
          const p = pct(g.currentCount, g.targetCount);
          const pic = parsePicture(g.userPicture);
          return (
            <div className="gt-pinned-item" key={g.id}>
              <div style={{ width:24, height:24, borderRadius:"50%", overflow:"hidden", background:lighten(g.color,.7), display:"grid", placeItems:"center", flexShrink:0, fontSize:".6rem", fontWeight:900, color:g.color, border:`1.5px solid ${g.color}` }}>
                {pic ? <img src={pic} alt={g.userName} style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={e => { e.currentTarget.style.display="none"; }}/> : initials(g.userName)}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div className="gt-pinned-label" style={{ overflow:"hidden", textOverflow:"ellipsis" }}>{g.label}</div>
                <div className="gt-pinned-prog"><div className="gt-pinned-fill" style={{ width:`${p}%`, background:g.color }}/></div>
              </div>
              <span style={{ fontSize:".72rem", fontWeight:950, color:g.color, flexShrink:0 }}>{p}%</span>
            </div>
          );
        })}
        <div style={{ display:"flex", gap:6, marginLeft:"auto", flexShrink:0 }}>
          <button className="gt-icon-btn" title="Modo visualização" onClick={() => setFsOpen(true)}><FiMonitor style={{ fontSize:14 }}/></button>
          <button className="gt-icon-btn" title="Gerir metas" onClick={() => setOpen(true)}><FiEdit2 style={{ fontSize:14 }}/></button>
        </div>
      </div>
    );
  };

  // ── GoalCard ──
  const GoalCard = ({ goal: g }) => {
    const [editing, setEditing]         = useState(false);
    const [localCurrent, setLocalCurrent] = useState(String(g.currentCount));
    const p        = pct(g.currentCount, g.targetCount);
    const dl       = daysLeft(g.endDate);
    const isPinned = pinned.includes(g.id);
    const commitCurrent = () => { updateCurrent(g.id, localCurrent); setEditing(false); };

    return (
      <div className="gt-goal-card" style={{ borderLeft:`4px solid ${g.color}` }}>
        <div className="gt-goal-header">
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:950, fontSize:".92rem", marginBottom:3 }}>{g.label}</div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {g.startDate && (
                <span className="gt-badge" style={{ background:lighten(g.color,.88), color:g.color, borderColor:lighten(g.color,.6) }}>
                  <FiCalendar style={{ fontSize:11 }}/> {fmtDate(g.startDate)} → {fmtDate(g.endDate)}
                </span>
              )}
              {dl !== null && (
                <span className="gt-badge" style={{ background:dl<7?"#fee2e2":dl<30?"#fef3c7":"#dbeafe", color:dl<7?"#991b1b":dl<30?"#92400e":"#1d4ed8", borderColor:"transparent" }}>
                  {dl > 0 ? `${dl}d restantes` : dl === 0 ? "Hoje!" : "Expirada"}
                </span>
              )}
            </div>
          </div>
          <div style={{ display:"flex", gap:6, flexShrink:0 }}>
            <Tooltip title={isPinned ? "Desafixar do topo" : "Afixar no topo"} arrow>
              <button className="gt-icon-btn" onClick={() => togglePin(g.id)} style={isPinned ? { background:lighten(g.color,.8), borderColor:g.color } : {}}>
                {isPinned ? <FiEye style={{ fontSize:14, color:g.color }}/> : <FiEyeOff style={{ fontSize:14 }}/>}
              </button>
            </Tooltip>
            <Tooltip title="Editar meta" arrow>
              <button className="gt-icon-btn" onClick={() => openEdit(g)}><FiEdit2 style={{ fontSize:14 }}/></button>
            </Tooltip>
            <Tooltip title="Remover meta" arrow>
              <button className="gt-icon-btn" onClick={() => deleteGoal(g.id)} style={{ borderColor:"rgba(239,68,68,.3)" }}><FiTrash2 style={{ fontSize:14, color:"#ef4444" }}/></button>
            </Tooltip>
          </div>
        </div>
        <ProgressBar current={g.currentCount} target={g.targetCount} color={g.color} picUrl={g.userPicture} name={g.userName} height={14}/>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {editing ? (
              <>
                <input className="gt-input" type="number" min="0" style={{ width:90 }} value={localCurrent}
                  onChange={e => setLocalCurrent(e.target.value)} onKeyDown={e => e.key === "Enter" && commitCurrent()} autoFocus/>
                <button className="gt-btn sm primary" onClick={commitCurrent}><FiSave/> OK</button>
                <button className="gt-btn sm" onClick={() => { setLocalCurrent(String(g.currentCount)); setEditing(false); }}>Cancelar</button>
              </>
            ) : (
              <>
                <span style={{ fontSize:".88rem", fontWeight:950, color:g.color }}>{g.currentCount} / {g.targetCount}</span>
                <button className="gt-btn sm" onClick={() => { setLocalCurrent(String(g.currentCount)); setEditing(true); }}>
                  <FiEdit2 style={{ fontSize:12 }}/> Actualizar
                </button>
              </>
            )}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:42, height:42, borderRadius:"50%", background:lighten(g.color,.88), border:`2px solid ${g.color}`, display:"grid", placeItems:"center", fontSize:".82rem", fontWeight:950, color:g.color }}>{p}%</div>
            {g.note && <span style={{ fontSize:".76rem", color:"var(--muted)", fontStyle:"italic", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{g.note}</span>}
          </div>
        </div>
      </div>
    );
  };

  // ── Overview ──
  const Overview = () => (
    <>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, gap:10, flexWrap:"wrap" }}>
        <div>
          <div style={{ fontWeight:950, fontSize:"1rem" }}>Visão geral das metas</div>
          <div style={{ fontSize:".8rem", color:"var(--muted)", fontWeight:650 }}>{goals.length} metas guardadas</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {pinnedGoals.length > 0 && (
            <Tooltip title="Abrir modo visualização" arrow>
              <button className="gt-btn" onClick={() => { setOpen(false); setFsOpen(true); }}><FiMonitor style={{ fontSize:15 }}/> Visualizar</button>
            </Tooltip>
          )}
          <button className="gt-btn primary" onClick={openNew}><FiPlus/> Nova meta</button>
        </div>
      </div>
      {goals.length === 0 ? (
        <div className="gt-no-goals"><FiTarget style={{ fontSize:32, opacity:.4, display:"block", margin:"0 auto 10px" }}/>Nenhuma meta criada ainda.<br/>Clique em <b>Nova meta</b> para começar.</div>
      ) : (
        Object.entries(goalsByUser).map(([uid, data]) => (
          <div key={uid} style={{ marginBottom:20 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
              <Avatar src={parsePicture(data.userPicture)||undefined} sx={{ width:32, height:32, fontSize:".78rem", fontWeight:900 }}>{initials(data.userName)}</Avatar>
              <div style={{ fontWeight:950, fontSize:".9rem" }}>{data.userName}</div>
              <span style={{ fontSize:".72rem", color:"var(--muted)", fontWeight:650 }}>{data.goals.length} meta(s)</span>
            </div>
            {data.goals.map(g => <GoalCard key={g.id} goal={g}/>)}
          </div>
        ))
      )}
    </>
  );

  // ── GoalForm ──
  const GoalForm = () => (
    <>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
        <button className="gt-btn sm" onClick={() => { resetForm(); setTab("overview"); }}>← Voltar</button>
        <span style={{ fontWeight:950, fontSize:".95rem" }}>{editingGoal ? "Editar meta" : "Nova meta"}</span>
      </div>
      <div className="gt-form-card">
        <div className="gt-section-title"><FiUsers style={{ verticalAlign:-2 }}/> Selecionar utilizador</div>
        {loadingUsers ? (
          <div style={{ fontSize:".82rem", color:"var(--muted)" }}>A carregar utilizadores…</div>
        ) : (
          <div className="gt-user-row">
            {users.map(u => (
              <div key={u.id} className={`gt-user-card ${selUser?.id === u.id ? "selected" : ""}`} onClick={() => setSelUser(u)}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <Avatar src={parsePicture(u.picture)||undefined} sx={{ width:38, height:38, fontSize:".8rem", fontWeight:900 }}>{initials(u.name)}</Avatar>
                  <div><div style={{ fontWeight:950, fontSize:".86rem" }}>{u.name}</div><div style={{ fontSize:".74rem", color:"var(--muted)" }}>{u.charge||u.email}</div></div>
                </div>
              </div>
            ))}
            {users.length === 0 && (
              <div style={{ fontSize:".82rem", color:"var(--muted)" }}>
                Nenhum utilizador.
                <button className="gt-btn sm" style={{ marginLeft:8 }} onClick={fetchUsers}><FiRefreshCw style={{ fontSize:13 }}/> Recarregar</button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="gt-form-card">
        <div className="gt-section-title"><FiAward style={{ verticalAlign:-2 }}/> Detalhes da meta</div>
        <div style={{ marginBottom:10 }}>
          <div className="gt-label">Título *</div>
          <input className="gt-input" placeholder="Ex.: Clientes angariados em Maio" value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))}/>
        </div>
        <div className="gt-grid-2" style={{ marginBottom:10 }}>
          <div><div className="gt-label">Target (unidades) *</div><input className="gt-input" type="number" min="1" placeholder="Ex.: 30" value={form.targetCount} onChange={e => setForm(p => ({ ...p, targetCount: e.target.value }))}/></div>
          <div><div className="gt-label">Progresso actual</div><input className="gt-input" type="number" min="0" placeholder="0" value={form.currentCount} onChange={e => setForm(p => ({ ...p, currentCount: e.target.value }))}/></div>
        </div>
        <div className="gt-grid-2" style={{ marginBottom:10 }}>
          <div><div className="gt-label">Data início</div><input className="gt-input" type="date" value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))}/></div>
          <div><div className="gt-label">Data fim</div><input className="gt-input" type="date" value={form.endDate} onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))}/></div>
        </div>
        <div style={{ marginBottom:10 }}>
          <div className="gt-label">Nota</div>
          <textarea className="gt-input" rows={2} placeholder="Contexto, observações…" value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} style={{ resize:"vertical" }}/>
        </div>
        <div>
          <div className="gt-label">Cor da meta</div>
          <div className="gt-color-row">
            {PALETTE.map(c => (
              <div key={c} className={`gt-color-dot ${form.color === c ? "sel" : ""}`}
                style={{ background:c, borderColor: form.color === c ? "#0f172a" : "transparent" }}
                onClick={() => setForm(p => ({ ...p, color: c }))}/>
            ))}
          </div>
        </div>
      </div>
      {selUser && form.targetCount && (
        <div className="gt-form-card">
          <div className="gt-section-title"><FiTrendingUp style={{ verticalAlign:-2 }}/> Pré-visualização</div>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
            <Avatar src={parsePicture(selUser.picture)||undefined} sx={{ width:30, height:30, fontSize:".72rem" }}>{initials(selUser.name)}</Avatar>
            <span style={{ fontWeight:950, fontSize:".86rem" }}>{selUser.name}</span>
            <span style={{ fontSize:".78rem", color:"var(--muted)" }}>— {form.label||"Meta"}</span>
          </div>
          <ProgressBar current={Number(form.currentCount)||0} target={Number(form.targetCount)||1} color={form.color} picUrl={selUser.picture} name={selUser.name} height={14}/>
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}>
            <span style={{ fontSize:".78rem", color:"var(--muted)" }}>{Number(form.currentCount)||0} / {Number(form.targetCount)||0}</span>
            <span style={{ fontSize:".78rem", fontWeight:950, color:form.color }}>{pct(Number(form.currentCount)||0, Number(form.targetCount)||1)}%</span>
          </div>
        </div>
      )}
      <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
        <button className="gt-btn" onClick={() => { resetForm(); setTab("overview"); }}>Cancelar</button>
        <button className="gt-btn primary" onClick={saveGoal}><FiSave/> {editingGoal ? "Guardar alterações" : "Criar meta"}</button>
      </div>
    </>
  );

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{css}</style>

      {/* IDLE QUOTE SCREEN — fires after 5min inactivity */}
      <IdleQuoteScreen />

      {/* PINNED BAR */}
      <PinnedBar />

      {/* GOALS FULLSCREEN */}
      {fsOpen && <FullscreenOverlay goals={goals} pinned={pinned} onClose={() => setFsOpen(false)} />}

      {/* FAB GROUP */}
      <div className="gt-fab-group">
        <Tooltip title={pinnedGoals.length > 0 ? "Modo visualização (TV / Monitor)" : "Afixe metas para activar a visualização"} arrow placement="left">
          <button
            className={`gt-fab-fs ${fsOpen ? "active-fs" : ""}`}
            onClick={() => pinnedGoals.length > 0 ? setFsOpen(true) : toast.info("Afixe pelo menos uma meta para usar este modo.")}
            aria-label="Modo visualização fullscreen"
          >
            <FiMonitor />
          </button>
        </Tooltip>
        <Tooltip title="Gestor de metas" arrow placement="left">
          <button className="gt-fab" onClick={() => setOpen(true)} aria-label="Abrir gestor de metas"><FiTarget /></button>
        </Tooltip>
      </div>

      {/* BACKDROP */}
      <div className={`gt-backdrop ${open ? "open" : ""}`} onClick={() => { setOpen(false); resetForm(); setTab("overview"); }} />

      {/* SIDE MODAL */}
      <div className={`gt-modal ${open ? "open" : ""}`} aria-modal="true" role="dialog">
        <div className="gt-header">
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:38, height:38, borderRadius:12, background:"var(--primary-soft,#ebe9ff)", border:"1px solid var(--primary-border,#d1ceff)", display:"grid", placeItems:"center", color:"var(--primary,#6052ff)" }}>
              <FiTarget style={{ fontSize:18 }}/>
            </div>
            <div>
              <div style={{ fontWeight:950, fontSize:"1rem" }}>Gestor de Metas</div>
              <div style={{ fontSize:".78rem", color:"var(--muted)", fontWeight:650 }}>{goals.length} meta(s) · {pinned.length} afixada(s)</div>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {pinnedGoals.length > 0 && (
              <Tooltip title="Abrir modo visualização" arrow>
                <button className="gt-icon-btn" onClick={() => { setOpen(false); setFsOpen(true); }}><FiMonitor style={{ fontSize:14 }}/></button>
              </Tooltip>
            )}
            <Tooltip title="Recarregar utilizadores" arrow>
              <button className="gt-icon-btn" onClick={fetchUsers} disabled={loadingUsers}><FiRefreshCw style={{ fontSize:14 }}/></button>
            </Tooltip>
            <button className="gt-icon-btn" onClick={() => { setOpen(false); resetForm(); setTab("overview"); }} aria-label="Fechar"><FiX style={{ fontSize:16 }}/></button>
          </div>
        </div>

        <div className="gt-body">
          <div className="gt-tabs">
            <button className={`gt-tab ${tab === "overview" ? "active" : ""}`} onClick={() => setTab("overview")}><FiTrendingUp/> Visão geral</button>
            <button className={`gt-tab ${tab === "new" ? "active" : ""}`} onClick={openNew}><FiPlus/> {editingGoal ? "Editar meta" : "Nova meta"}</button>
            {pinnedGoals.length > 0 && (
              <button className={`gt-tab ${tab === "pinned" ? "active" : ""}`} onClick={() => setTab("pinned")}><FiEye/> Afixadas ({pinnedGoals.length})</button>
            )}
          </div>

          {tab === "overview" && <Overview />}
          {tab === "new"      && <GoalForm />}
          {tab === "pinned"   && (
            <>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, gap:10 }}>
                <div>
                  <div style={{ fontWeight:950, fontSize:".95rem" }}>Metas afixadas no topo</div>
                  <div style={{ fontSize:".8rem", color:"var(--muted)", fontWeight:650 }}>Visíveis acima do navbar e no modo visualização.</div>
                </div>
                <button className="gt-btn" onClick={() => { setOpen(false); setFsOpen(true); }}><FiMonitor style={{ fontSize:14 }}/> Visualizar</button>
              </div>
              {pinnedGoals.length === 0 ? (
                <div className="gt-no-goals">Nenhuma meta afixada.<br/>Clique no ícone <FiEye style={{ verticalAlign:-2 }}/> numa meta para a afixar.</div>
              ) : (
                pinnedGoals.map(g => {
                  const p = pct(g.currentCount, g.targetCount);
                  const pic = parsePicture(g.userPicture);
                  return (
                    <div key={g.id} className="gt-goal-card" style={{ borderLeft:`4px solid ${g.color}` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                        <Avatar src={pic||undefined} sx={{ width:32, height:32, fontSize:".72rem" }}>{initials(g.userName)}</Avatar>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:950, fontSize:".9rem" }}>{g.label}</div>
                          <div style={{ fontSize:".76rem", color:"var(--muted)" }}>{g.userName}</div>
                        </div>
                        <span style={{ fontWeight:950, fontSize:".92rem", color:g.color }}>{p}%</span>
                        <Tooltip title="Desafixar" arrow>
                          <button className="gt-icon-btn" onClick={() => togglePin(g.id)}><FiEyeOff style={{ fontSize:14 }}/></button>
                        </Tooltip>
                      </div>
                      <ProgressBar current={g.currentCount} target={g.targetCount} color={g.color} picUrl={g.userPicture} name={g.userName} height={10}/>
                      <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}>
                        <span style={{ fontSize:".76rem", color:"var(--muted)" }}>{g.currentCount} / {g.targetCount}{g.endDate && ` · até ${fmtDate(g.endDate)}`}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}